import React from 'react';
import { Grid, Modal, Box, Typography, CircularProgress, Divider, ListItem, ListItemText, Tooltip } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { makeStyles, useTheme } from '@mui/styles';
import { Link as RouterLink } from 'react-router-dom';

import { DataAPIContext, UPDATE } from '../../api';
import useBatchDelete from '../../bg_tasks/useBatchDelete';
import PageHeading from '../../components/PageHeading';
import PageMeta from '../../components/PageMeta';
import RecordList from '../../components/RecordList';
import { getLicenseeColumn } from '../../components/franchiseColumns';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import FixedLinearProgress from '../../components/FixedLinearProgress';
import { ContextStore } from '../../store/contextStore';
import { useIsWidthUp } from '../../utils';
import WaiverListToolbar from './WaiverListToolbar';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteIconButton from '../../components/DeleteIconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Switch from '@mui/material/Switch';

const useStyles = makeStyles((theme) => ({
    recordList: {
        height: `calc(${theme.cp.contentHeight} - 6.5rem) !important`,
        marginTop: '-12px',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
    associatedTripsModal: {
        position: 'absolute',
        backgroundColor: 'white',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 350,
        boxShadow: 24,
        padding: '1rem',
        borderRadius: '4px',
    },
    listItem: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: theme.spacing(1),
        '& .MuiListItemText-root': {
            width: '100%',
            margin: 0,
        },
    },
    mobileActions: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        marginTop: theme.spacing(0.5),
    },
}));

const ConfirmationDialogText = {
    delete_waiver: {
        title: 'Delete Waiver',
        body: 'Are you sure you want to delete this waiver?',
        okButtonText: 'Delete',
        cancelButtonText: 'Cancel',
    },
    delete_waivers: {
        title: 'Delete Waivers',
        body: 'Are you sure you want to delete these waivers?',
        okButtonText: 'Delete',
        cancelButtonText: 'Cancel',
    },
};

const WaiverList = () => {
    const dataAPI = React.useContext(DataAPIContext);
    const history = useHistory();
    const theme = useTheme();
    const { hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    const hasReadPerm = hasPerm('waiver', 'read');
    const hasWritePerm = hasPerm('waiver', 'write');
    const hasDeletePerm = hasPerm('waiver', 'delete');

    const [syncActive, setSyncActive] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const urlParams = new URLSearchParams(window.location.search);
    const [query, setQuery] = React.useState(urlParams.get('query') || null);

    // TODO: remove this once we have a proper resource for waivers
    //const resource = `${tourOperator.code}/waiver`;
    const resource = 'waiver';

    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const isSmallScreen = !useIsWidthUp('sm');
    const [confirmationDialogAction, setConfirmationDialogAction] = React.useState(null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const [associatedTripsAndUsers, setAssociatedTripsAndUsers] = React.useState([]);
    const [showAssociatedTripsModal, setShowAssociatedTripsModal] = React.useState(false);

    const onQueryChange = (query) => {
        setQuery(query);
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('query', query);
        history.push(`${window.location.pathname}?${searchParams.toString()}`);
    };

    const onSelectionModelChange = (ids, selection) => {
        let selectedIds = isSmallScreen ? ids : selection;
        setSelectedIds(selectedIds);
    };

    // CP-7771: bulk delete runs as a background task; the grid refreshes once it has finished.
    const refreshList = React.useCallback(() => setLastUpdate(new Date().toString()), []);
    const { deleteRecords } = useBatchDelete({ resource, names: ['waiver', 'waivers'], onDeleted: refreshList });
    const doDeleteWaiverList = (idlist) => {
        setSyncActive(true);
        deleteRecords(idlist)
            .then((accepted) => {
                if (accepted) setIdsToDelete(null);
            })
            .finally(() => {
                setSelectedIds([]);
                setSyncActive(false);
            });
    };

    const onDeleteWaiverList = async (idlist) => {
        setAssociatedTripsAndUsers([]);
        setIdsToDelete(idlist);
        setConfirmationDialogAction(idlist.length > 1 ? 'delete_waivers' : 'delete_waiver');
    };

    const confirmationDialogCancel = () => {
        setConfirmationDialogAction(null);
    };

    const handleEnabledChange = async (id, currentEnabledStatus) => {
        setSyncActive(true);
        try {
            await dataAPI(UPDATE, resource, { id, data: { enabled: !currentEnabledStatus } });
            enqueueSnackbar('Waiver status updated successfully', { variant: 'success' });
            setLastUpdate(new Date().toString()); // Trigger list refresh
        } catch (error) {
            enqueueSnackbar('Error updating waiver status', { variant: 'error' });
        } finally {
            setSyncActive(false);
        }
    };

    const confirmationDialogConfirm = () => {
        switch (confirmationDialogAction) {
            case 'delete_waiver':
            case 'delete_waivers':
                doDeleteWaiverList(idsToDelete);
                break;
            default:
                break;
        }
        confirmationDialogCancel();
    };

    const listItemRenderer = (row, idx, rows) => {
        const languages = row.text && row.text.values ? Object.keys(row.text.values).join(', ').toUpperCase() : '-';
        const createdDate = new Date(row.created_ts).toLocaleString();

        return (
            <React.Fragment>
                <ListItem className={classes.listItem}>
                    <ListItemText
                        primary={row.name}
                        secondary={
                            <div>
                                <Typography variant="body2" color="textSecondary">
                                    Languages: <strong>{languages}</strong>
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Used in trips: <strong>{row.used_in_trips || 0}</strong>
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Signatures: <strong>{row.signatures || 0}</strong>
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Created: <strong>{createdDate}</strong>
                                </Typography>
                            </div>
                        }
                    />

                    {isSmallScreen && <Box sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.12)', margin: '8px 0', width: '100%' }} />}

                    <div className={classes.mobileActions}>
                        <Box display="flex" alignItems="center">
                            <Switch
                                checked={row.enabled}
                                onChange={(event) => {
                                    event.stopPropagation();
                                    handleEnabledChange(row.id, row.enabled);
                                }}
                                disabled={!hasWritePerm}
                                color="primary"
                                size="medium"
                                sx={{
                                    marginLeft: '-12px',
                                    marginBottom: '6px',
                                }}
                            />
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                ml={0.5}
                                fontSize={14}
                                sx={{
                                    paddingLeft: '0px',
                                    marginBottom: '6px',
                                }}
                            >
                                Enabled
                            </Typography>
                        </Box>
                        <Box display="flex">
                            {hasReadPerm && (
                                <IconButton size="small">
                                    <RouterLink to={`/waivers/${row.id}/preview`}>
                                        <VisibilityIcon color="primary" fontSize="small" />
                                    </RouterLink>
                                </IconButton>
                            )}
                            {hasWritePerm && (
                                <IconButton size="small">
                                    <RouterLink to={`/waivers/${row.id}`}>
                                        <EditIcon color="primary" fontSize="small" />
                                    </RouterLink>
                                </IconButton>
                            )}
                            <DeleteIconButton
                                allowed={hasDeletePerm}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onDeleteWaiverList([row.id]);
                                }}
                                sx={{
                                    paddingBottom: '10px',
                                }}
                            />
                        </Box>
                    </div>
                </ListItem>
                {idx < rows.length - 1 ? <Divider /> : null}
            </React.Fragment>
        );
    };

    return (
        <>
            <Modal open={showAssociatedTripsModal} onClose={() => setShowAssociatedTripsModal(false)} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Box className={classes.associatedTripsModal}>
                    {syncActive && (
                        <div style={{ marginTop: '1rem', width: '100%', display: 'flex', justifyContent: 'center', paddingTop: '1rem' }}>
                            <CircularProgress />
                        </div>
                    )}
                    {associatedTripsAndUsers && associatedTripsAndUsers.length > 0 && (
                        <div>
                            <Typography variant="h5">This waiver cannot be deleted since it is part of the following tours:</Typography>
                            <div style={{ maxHeight: '20vh', overflow: 'auto' }}>
                                {associatedTripsAndUsers.map((atu) => (
                                    <Typography key={atu?.group_tour?.id} style={{ margin: '1rem 0' }}>
                                        {atu?.group_tour?.name}{' '}
                                        <RouterLink style={{ color: theme.palette.primary, textDecoration: 'none' }} to={`/trip/${atu?.group_tour?.id}`}>
                                            {atu?.group_tour?.id}
                                        </RouterLink>
                                    </Typography>
                                ))}
                            </div>
                        </div>
                    )}
                </Box>
            </Modal>
            <PageMeta title="Waivers" />
            {confirmationDialogAction && (
                <ConfirmationDialog
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {ConfirmationDialogText[confirmationDialogAction].body}
                    {syncActive && (
                        <div style={{ marginTop: '1rem', width: '100%', display: 'flex', justifyContent: 'center', paddingTop: '1rem' }}>
                            <CircularProgress />
                        </div>
                    )}
                </ConfirmationDialog>
            )}
            <FixedLinearProgress visibility={syncActive} />
            <Grid item xs={12} container direction="row" spacing={2} p={2}>
                <Grid item xs={12}>
                    <PageHeading sx={{ mb: 1 }}>Waivers</PageHeading>
                    <WaiverListToolbar
                        query={query}
                        setQuery={setQuery}
                        resource={resource}
                        selectedIds={selectedIds}
                        onQueryChange={onQueryChange}
                        onDeleteWaiver={onDeleteWaiverList}
                        isSmallScreen={isSmallScreen}
                    />
                </Grid>

                <Grid item xs={12} sx={{
                    paddingTop: isSmallScreen ? '0px !important' : undefined,
                }}>
                    <RecordList
                        className={classes.recordList}
                        resource={resource}
                        disableSelectionOnClick={false}
                        checkboxSelection={hasDeletePerm}
                        sortModel={[{ field: 'created_ts', sort: 'desc' }]}
                        onSelectionModelChange={onSelectionModelChange}
                        deps={lastUpdate}
                        query={query}
                        limitHeightForMobile={'calc(100vh - 13rem)'}
                        listItemRenderer={isSmallScreen ? listItemRenderer : null}
                        perPage={100}
                        columns={[
                            ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
                            { field: 'name', headerName: 'Name', filterable: true, sortable: true, flex: 1 },
                            {
                                field: 'sequence',
                                headerName: 'Order',
                                filterable: false,
                                sortable: true,
                                width: 80,
                                type: 'number',
                                hide: true
                            },
                            {
                                field: 'enabled',
                                headerName: 'Enabled',
                                filterable: false,
                                sortable: true,
                                width: 100,
                                renderCell: (params) => (
                                    <Switch
                                        checked={params.row.enabled}
                                        onChange={(event) => {
                                            event.stopPropagation();
                                            handleEnabledChange(params.row.id, params.row.enabled);
                                        }}
                                        disabled={!hasWritePerm}
                                        color="primary"
                                        onClick={(event) => event.stopPropagation()}
                                    />
                                )
                            },
                            {
                                field: 'languages',
                                headerName: 'Languages',
                                filterable: false,
                                sortable: false,
                                width: 100,
                                renderCell: (params) => {
                                    if (params.row.text && params.row.text.values) {
                                        const languages = Object.keys(params.row.text.values);
                                        const languageList = languages.join(', ').toUpperCase();
                                        return (
                                            <Tooltip title={languageList} arrow>
                                                <span>{languages.length}</span>
                                            </Tooltip>
                                        );
                                    }
                                    return '0';
                                }
                            },
                            { field: 'created_ts', headerName: 'Created On', filterable: false, sortable: true, width: 140 },
                            {
                                field: 'used_in_trips',
                                headerName: 'Used in Trips',
                                filterable: false,
                                sortable: false,
                                width: 120,
                                renderCell: (params) => {
                                    const tripsCount = params.row.used_in_trips || 0;
                                    const trips = params.row.trips || [];

                                    if (tripsCount === 0 || trips.length === 0) {
                                        return '0';
                                    }

                                    const tooltipContent = (
                                        <div>
                                            {trips.map((tripId) => (
                                                <div key={tripId} style={{ marginBottom: '4px' }}>
                                                    <a
                                                        href={`/trip/${tripId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: '#fff',
                                                            textDecoration: 'none',
                                                            fontSize: '0.875rem'
                                                        }}
                                                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                                                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                                                    >
                                                        Trip {tripId}
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    );

                                    return (
                                        <Tooltip title={tooltipContent} arrow>
                                            <span style={{ cursor: 'pointer' }}>{tripsCount}</span>
                                        </Tooltip>
                                    );
                                }
                            },
                            {
                                field: 'signatures',
                                headerName: 'Signatures',
                                filterable: false,
                                sortable: false,
                                width: 120,
                                renderCell: (params) => {
                                    const signaturesCount = params.row.signatures || 0;
                                    const signatureUsers = params.row.signatures_users || [];

                                    if (signaturesCount === 0 || signatureUsers.length === 0) {
                                        return '0';
                                    }

                                    const tooltipContent = (
                                        <div>
                                            {signatureUsers.map((userId) => (
                                                <div key={userId} style={{ marginBottom: '4px' }}>
                                                    <a
                                                        href={`/user/${userId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: '#fff',
                                                            textDecoration: 'none',
                                                            fontSize: '0.875rem'
                                                        }}
                                                        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                                                        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                                                    >
                                                        User {userId}
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    );

                                    return (
                                        <Tooltip title={tooltipContent} arrow>
                                            <span style={{ cursor: 'pointer' }}>{signaturesCount}</span>
                                        </Tooltip>
                                    );
                                }
                            },
                            {
                                field: 'buttons',
                                headerName: 'Action',
                                sortable: false,
                                width: 150,
                                renderCell: (p) => {
                                    return (
                                        <Grid container alignItems="center">
                                            {hasReadPerm && (
                                                <Grid item>
                                                    <IconButton>
                                                        <RouterLink to={`/waivers/${p.id}/preview`}>
                                                            <VisibilityIcon color="primary" />
                                                        </RouterLink>
                                                    </IconButton>
                                                </Grid>
                                            )}
                                            {hasWritePerm && (
                                                <Grid item>
                                                    <IconButton>
                                                        <RouterLink to={`/waivers/${p.id}`}>
                                                            <EditIcon color="primary" fontSize="small" />
                                                        </RouterLink>
                                                    </IconButton>
                                                </Grid>
                                            )}
                                            <Grid item>
                                                <DeleteIconButton
                                                    allowed={hasDeletePerm}
                                                    size="medium"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        onDeleteWaiverList([p.id]);
                                                    }}
                                                    sx={{
                                                        paddingBottom: '10px',
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    );
                                },
                            },
                        ]}
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default WaiverList; 